import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";
import { BrandMark } from "./BrandMark";
import { Sidebar } from "./Sidebar";
import type { RoleConfig } from "./roles";

export function MobileDrawer({
  open,
  onClose,
  role,
}: {
  open: boolean;
  onClose: () => void;
  role: RoleConfig;
}) {
  const [mounted, setMounted] = useState(false);
  const [entered, setEntered] = useState(false);

  useEffect(() => {
    if (open) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- mount→rAF→enter необходимо для CSS-анимации входа
      setMounted(true);
      const r = requestAnimationFrame(() => setEntered(true));
      return () => cancelAnimationFrame(r);
    } else if (mounted) {
      setEntered(false);
      const t = setTimeout(() => setMounted(false), 220);
      return () => clearTimeout(t);
    }
  }, [open, mounted]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [open, onClose]);

  if (!mounted) return null;

  return createPortal(
    <div className="fixed inset-0 z-40 md:hidden">
      <div
        onClick={onClose}
        className={
          "absolute inset-0 bg-slate-900/40 backdrop-blur-[2px] transition-opacity duration-200 " +
          (entered ? "opacity-100" : "opacity-0")
        }
      />
      <aside
        className={
          "absolute left-0 top-0 h-full w-[280px] max-w-[85vw] overflow-y-auto bg-[var(--color-surface)] shadow-2xl transition-transform duration-200 " +
          (entered ? "translate-x-0" : "-translate-x-full")
        }
      >
        <div className="flex h-14 items-center justify-between border-b border-[var(--color-border)] px-4">
          <BrandMark />
          <button
            onClick={onClose}
            className="grid h-9 w-9 place-items-center rounded-lg text-[var(--color-fg-muted)] transition-colors hover:bg-[var(--color-surface-2)] hover:text-[var(--color-fg)]"
            aria-label="Закрыть"
          >
            <X size={18} />
          </button>
        </div>
        <div className="px-3 pb-3 pt-5">
          <div className="px-3 pb-2 text-[10.5px] font-medium uppercase tracking-[0.08em] text-[var(--color-fg-faint)]">
            {role.breadcrumb}
          </div>
          <Sidebar role={role} onItemClick={onClose} />
        </div>
      </aside>
    </div>,
    document.body,
  );
}
